import { call, put, takeEvery } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { metafieldService } from "@/services/metafieldService";
import {
  CreateMetafieldDto,
  UpdateMetafieldDto,
} from "@/types/models/metafield";
import { toApiError } from "@/lib/errorHelpers";
import {
  fetchMetafieldsRequest,
  fetchMetafieldsSuccess,
  fetchMetafieldsFailure,
  createMetafieldRequest,
  createMetafieldSuccess,
  createMetafieldFailure,
  updateMetafieldRequest,
  updateMetafieldSuccess,
  updateMetafieldFailure,
  deleteMetafieldRequest,
  deleteMetafieldSuccess,
  deleteMetafieldFailure,
  checkMetafieldKeyRequest,
  checkMetafieldKeySuccess,
  checkMetafieldKeyFailure,
} from "@/store/slices/metafield.slice";

function* fetchMetafieldsSaga(
  action: PayloadAction<{ page?: number; limit?: number }>,
): Generator<any, void, any> {
  try {
    const response: any = yield call(
      metafieldService.getMetafields,
      action.payload,
    );

    yield put(
      fetchMetafieldsSuccess({
        data: response.data || response,
        pagination: response.pagination || {
          page: action.payload.page || 1,
          totalPages: 1,
          pageSize: action.payload.limit || 10,
          total: response.data?.length || response.length || 0,
        },
      }),
    );
  } catch (error: any) {
    yield put(fetchMetafieldsFailure(toApiError(error)));
  }
}

function* createMetafieldSaga(
  action: PayloadAction<CreateMetafieldDto>,
): Generator<any, void, any> {
  try {
    const response: any = yield call(
      metafieldService.createMetafield,
      action.payload,
    );

    yield put(createMetafieldSuccess(response.data || response));
  } catch (error: any) {
    yield put(createMetafieldFailure(toApiError(error)));
  }
}

function* updateMetafieldSaga(
  action: PayloadAction<{ key: string; data: UpdateMetafieldDto }>,
): Generator<any, void, any> {
  try {
    const response: any = yield call(
      metafieldService.updateMetafield,
      action.payload.key,
      action.payload.data,
    );

    yield put(updateMetafieldSuccess(response.data || response));
  } catch (error: any) {
    yield put(updateMetafieldFailure(toApiError(error)));
  }
}

function* deleteMetafieldSaga(
  action: PayloadAction<{ id: string }>,
): Generator<any, void, any> {
  try {
    yield call(metafieldService.deleteMetafield, action.payload.id);

    yield put(deleteMetafieldSuccess({ id: action.payload.id }));
  } catch (error: any) {
    yield put(deleteMetafieldFailure(toApiError(error)));
  }
}

function* checkMetafieldKeySaga(
  action: PayloadAction<{ key: string; applied_to: string }>,
): Generator<any, void, any> {
  try {
    const response: any = yield call(
      metafieldService.checkMetafieldKeyUniqueness,
      action.payload.key,
      action.payload.applied_to,
    );

    yield put(
      checkMetafieldKeySuccess({
        key: action.payload.key,
        isUnique: response.data?.isUnique ?? response.isUnique ?? true,
      }),
    );
  } catch (error: any) {
    yield put(
      checkMetafieldKeyFailure({
        key: action.payload.key,
        error: toApiError(error)?.message || "Failed to check key uniqueness",
      }),
    );
  }
}

export function* metafieldSaga(): Generator<any, void, any> {
  yield takeEvery(fetchMetafieldsRequest.type, fetchMetafieldsSaga);
  yield takeEvery(createMetafieldRequest.type, createMetafieldSaga);
  yield takeEvery(updateMetafieldRequest.type, updateMetafieldSaga);
  yield takeEvery(deleteMetafieldRequest.type, deleteMetafieldSaga);
  yield takeEvery(checkMetafieldKeyRequest.type, checkMetafieldKeySaga);
}
